import { useEffect, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectComponentsProvider,
  ConnectPayouts,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import { X } from "lucide-react";
import api from "@/lib/api";
import { sound } from "@/lib/sound";

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// HonestPay-themed embedded payout management. Lets the host add/manage a debit
// card, edit their payout schedule, and view payouts — all in-app, no redirect
// to the Stripe hosted dashboard. Same dark modal style as OnboardingModal.
export default function ManagePayoutsModal({ open, onClose, onDone }) {
  const [instance, setInstance] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || instance || !PUBLISHABLE_KEY) return;
    const fetchClientSecret = async () => {
      const r = await api.post("/billing/connect/account-session-manage");
      const secret = r.data?.client_secret;
      if (!secret) throw new Error("no client_secret");
      return secret;
    };
    try {
      setInstance(
        loadConnectAndInitialize({
          publishableKey: PUBLISHABLE_KEY,
          fetchClientSecret,
          appearance: {
            overlays: "dialog",
            variables: {
              colorPrimary: "#a855f7",
              colorBackground: "#0e0a16",
              colorText: "#f5f3ff",
              colorSecondaryText: "#a1a1b3",
              colorBorder: "rgba(168,85,247,0.25)",
              colorDanger: "#f87171",
              buttonPrimaryColorBackground: "#7c3aed",
              buttonPrimaryColorText: "#ffffff",
              borderRadius: "14px",
              fontFamily: "Sora, Space Grotesk, sans-serif",
              spacingUnit: "9px",
            },
          },
        })
      );
    } catch {
      setError(true);
    }
  }, [open, instance]);

  const handleClose = async () => {
    sound.play("tap");
    try {
      const r = await api.get("/billing/connect/status");
      onDone?.(r.data);
    } catch {
      onDone?.(null);
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="manage-payouts-modal"
      className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-[460px] max-h-[88vh] overflow-y-auto hp-glass-strong rounded-3xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-extrabold text-white">Payout settings</p>
            <p className="text-xs text-muted-foreground">
              Add a debit card, edit your schedule &amp; view payouts
            </p>
          </div>
          <button
            data-testid="manage-payouts-close"
            onClick={handleClose}
            className="h-9 w-9 grid place-items-center rounded-full bg-white/5 text-muted-foreground hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {error || !PUBLISHABLE_KEY ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Couldn&apos;t open payout settings right now. Please try again.
          </p>
        ) : instance ? (
          <ConnectComponentsProvider connectInstance={instance}>
            <div className="space-y-4">
              <ConnectNotificationBanner />
              <ConnectPayouts />
            </div>
          </ConnectComponentsProvider>
        ) : (
          <div className="py-10 grid place-items-center">
            <div className="h-8 w-8 rounded-full border-2 border-[#a855f7]/40 border-t-[#a855f7] animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
